import _ from 'lodash';
import {
    CurrencyType,
    ProviderClaim,
    ProviderPatientStatement,
    ProviderProfessionalClaim,
} from '@enter/core';
import type {
    ClaimsTableRow,
    IPaymentData,
    IStatementData,
    PaymentsTableRow,
    ServicesTableRow,
} from './statement-details.types';
import { extractDiagnosisCodes, isInRange } from './utils/statement-filter.utils';
import { parseMoney } from './utils/statement-format.utils';
import {
    isStatementClaimPendingAdjudication,
    resolveStatementClaimFinancials,
} from './utils/statement-claim-financials.utils';

export const buildPaymentsData = (statement: ProviderPatientStatement | undefined): IPaymentData[] => {
    if (!statement) return [];

    const paymentSources = statement.paymentSources || [];
    const claims = statement.claims || [];

    return paymentSources.map(paymentSource => {
        const source: IPaymentData = { ...paymentSource };
        let totalRefund = 0;

        for (const claim of claims) {
            const patientPayments = claim.patientPayments || [];
            const refundForSource = patientPayments
                .filter(
                    patientPayment =>
                        patientPayment.collectionPaymentSourceId === paymentSource.id &&
                        patientPayment.amount &&
                        typeof patientPayment.amount.amount === 'string' &&
                        parseFloat(patientPayment.amount.amount) < 0,
                )
                .reduce((sum, patientPayment) => {
                    const value = parseFloat(patientPayment.amount.amount || '0');
                    return Number.isNaN(value) ? sum : sum + value;
                }, 0);

            totalRefund += refundForSource;
        }

        if (totalRefund !== 0) {
            source.refundAmount = {
                amount: (totalRefund * -1).toFixed(2),
                currency: CurrencyType.USD,
            };
        }

        return source;
    });
};

/** Scripted copied from Retool Patient Statement Details page. */
export const buildStatementLedgerRows = (
    statement: ProviderPatientStatement | undefined,
    professionals: ProviderProfessionalClaim[],
): IStatementData[] => {
    if (!statement) return [];

    const rows: IStatementData[] = [];
    const typeOrder: Record<string, number> = {
        unallocated: 1,
        claim: 2,
        line: 3,
        payment: 4,
        adjustment: 5,
        discount: 6,
        allocated: 7,
        balance: 8,
    };

    for (const claim of statement.claims || []) {
        const pendingMetadata = { pending: claim.pendingAdjudication };
        rows.push({
            id: `${claim.id}-claim`,
            claimId: claim.id,
            type: 'claim',
            date: claim.fromDos,
            description:
                'Claim #' +
                (claim.externalId || claim.id || '') +
                '\n' +
                'Provider(s): ' +
                (claim.renderingProviders || [])
                    .map(provider => `${provider.firstName}, ${provider.lastName}`)
                    .join(', '),
            metadata: pendingMetadata,
        });
    }

    for (const claim of statement.claims || []) {
        const pendingMetadata = { pending: claim.pendingAdjudication };
        if (claim.payerPayments) {
            for (let paymentIndex = 0; paymentIndex < claim.payerPayments.length; paymentIndex++) {
                const payment = claim.payerPayments[paymentIndex];
                rows.push({
                    id: `${claim.id}-payment-${paymentIndex}`,
                    claimId: claim.id,
                    type: 'payment',
                    date: claim.fromDos,
                    description: payment.insuranceName + ' Payment',
                    credit: payment.payerAmount,
                    metadata: pendingMetadata,
                });

                if (payment.adjustmentAmount) {
                    rows.push({
                        id: `${claim.id}-adjustment-${paymentIndex}`,
                        claimId: claim.id,
                        type: 'adjustment',
                        date: claim.fromDos,
                        description: payment.insuranceName + ' Adjustment',
                        credit: payment.adjustmentAmount,
                        metadata: pendingMetadata,
                    });
                }
            }
        }

        if (claim.balanceDue) {
            rows.push({
                id: `${claim.id}-balance`,
                claimId: claim.id,
                type: 'balance',
                date: claim.fromDos,
                description: 'Balance due on this claim',
                balance: claim.balanceDue,
                metadata: pendingMetadata,
            });
        }

        if (claim.patientPayments) {
            for (const patientPayment of claim.patientPayments) {
                rows.push({
                    id: `${claim.id}-allocated-${patientPayment.id}`,
                    claimId: claim.id,
                    type: 'allocated',
                    date: patientPayment.paymentDate,
                    description: 'Patient Payment for Claim ' + claim.externalId,
                    credit: patientPayment.amount,
                    metadata: {
                        ...pendingMetadata,
                        id: patientPayment.id,
                    },
                });
            }
        }

        if (claim.discountLines) {
            for (const discount of claim.discountLines) {
                rows.push({
                    id: `${claim.id}-discount-${discount.id}`,
                    claimId: claim.id,
                    type: 'discount',
                    date: claim.fromDos,
                    description: 'Discount',
                    credit: discount.amount,
                    metadata: pendingMetadata,
                });
            }
        }

        if (claim.serviceLines) {
            for (const line of claim.serviceLines) {
                const currentProfessional = _.find(professionals, { claimId: claim.id });
                let diagnosisCodes: string[] = [];
                if (currentProfessional && currentProfessional.professionalTemplate) {
                    const serviceLineDetails = _.find(currentProfessional.professionalTemplate.f24ServiceLine, {
                        id: line.id,
                    });
                    if (serviceLineDetails) {
                        const serviceLinePointers = serviceLineDetails.f24eDiagnosisPointers || [];
                        const diagnosisCodeMap = extractDiagnosisCodes(currentProfessional.professionalTemplate);
                        diagnosisCodes = serviceLinePointers
                            .map((letter: string) => diagnosisCodeMap[letter.toLowerCase()])
                            .filter(Boolean);
                    }
                }

                rows.push({
                    id: `${claim.id}-line-${line.id}`,
                    claimId: claim.id,
                    type: 'line',
                    date: claim.fromDos,
                    description: line.procedure + ' - ' + line.description,
                    diagnosisCodes,
                    charge: line.chargeAmount,
                    metadata: pendingMetadata,
                });
            }
        }
    }

    for (const source of statement.paymentSources || []) {
        if (parseFloat(source.unallocatedAmount?.amount || '0') > 0) {
            rows.push({
                id: `${source.id}-unallocated`,
                claimId: source.id,
                type: 'unallocated',
                date: source.paymentDate,
                description: 'Patient Payment (Unallocated)',
                credit: source.unallocatedAmount,
                balance: {
                    amount: (parseFloat(source.unallocatedAmount?.amount || '0') * -1).toFixed(2),
                    currency: source.unallocatedAmount?.currency || CurrencyType.USD,
                },
            });
        }
    }

    rows.sort((a, b) => {
        if (a.date !== b.date) {
            return new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime();
        }
        if (a.claimId !== b.claimId) {
            return (a.claimId || '').localeCompare(b.claimId || '');
        }
        return typeOrder[a.type] - typeOrder[b.type];
    });

    return rows;
};

export const filterStatementDataByDos = (
    statementData: IStatementData[],
    dosFilterFrom: Date | null,
    dosFilterTo: Date | null,
): IStatementData[] => statementData.filter(r => isInRange(r.date, dosFilterFrom, dosFilterTo));

export const filterPaymentsDataByDos = (
    paymentsData: IPaymentData[],
    statement: ProviderPatientStatement | undefined,
    dosFilterFrom: Date | null,
    dosFilterTo: Date | null,
): IPaymentData[] => {
    const sorted = [...paymentsData].sort((a, b) => {
        const aTime = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
        const bTime = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
        return bTime - aTime;
    });

    if (!dosFilterFrom && !dosFilterTo) return sorted;
    const statementClaims = statement?.claims || [];
    return sorted.filter(p => {
        const associatedClaims = statementClaims.filter(c =>
            (c.patientPayments || []).some(pp => pp.collectionPaymentSourceId === p.id),
        );
        if (associatedClaims.length === 0) return true;
        return associatedClaims.some(c => isInRange(c.fromDos, dosFilterFrom, dosFilterTo));
    });
};

export const filterClaimsByDos = (
    claims: ProviderClaim[] | undefined,
    dosFilterFrom: Date | null,
    dosFilterTo: Date | null,
): ProviderClaim[] =>
    (claims || [])
        .filter(c => isInRange(c.fromDos, dosFilterFrom, dosFilterTo))
        .sort((a, b) => {
            const aTime = a.fromDos ? new Date(a.fromDos).getTime() : 0;
            const bTime = b.fromDos ? new Date(b.fromDos).getTime() : 0;
            return bTime - aTime;
        });

export const buildClaimsById = (claims: ProviderClaim[] | undefined): Map<string, ProviderClaim> => {
    const map = new Map<string, ProviderClaim>();
    for (const claim of claims || []) {
        if (claim.id) map.set(claim.id, claim);
    }
    return map;
};

export const buildClaimsTableRows = (args: {
    statement: ProviderPatientStatement | undefined;
    claimsById: Map<string, ProviderClaim>;
    dosFilterFrom: Date | null;
    dosFilterTo: Date | null;
    getClaimRowHref?: (claim: ProviderClaim) => string;
}): ClaimsTableRow[] => {
    const { statement, claimsById, dosFilterFrom, dosFilterTo, getClaimRowHref } = args;

    return (statement?.claims || [])
        .filter(claim => isInRange(claim.fromDos, dosFilterFrom, dosFilterTo))
        .map(claim => {
            const providerClaim = claim.id ? claimsById.get(claim.id) : undefined;
            const { charge, insurance, adjustment, patientResp } = resolveStatementClaimFinancials(
                claim,
                providerClaim,
            );
            const maxPatientResp = providerClaim?.maximumGuarantorResponsibility
                ? parseMoney(providerClaim.maximumGuarantorResponsibility.amount)
                : undefined;
            const pending = isStatementClaimPendingAdjudication(claim, providerClaim);
            const payer =
                (claim.payerPayments || []).find(p => p.insuranceName)?.insuranceName ||
                providerClaim?.primaryPayerName ||
                '—';

            return {
                id: claim.id || claim.externalId || '',
                num: claim.externalId || claim.id || '—',
                statusLabel: pending
                    ? 'Pending adjudication'
                    : providerClaim?.claimResponsibilityType
                      ? String(providerClaim.claimResponsibilityType).replace(/_/g, ' ')
                      : 'Claim',
                payer,
                charge,
                insurance,
                adjustment,
                patientResp,
                maxPatientResp,
                dos: claim.fromDos,
                href: getClaimRowHref && providerClaim ? getClaimRowHref(providerClaim) : undefined,
            };
        })
        .sort((a, b) => {
            const aTime = a.dos ? new Date(a.dos).getTime() : 0;
            const bTime = b.dos ? new Date(b.dos).getTime() : 0;
            return bTime - aTime;
        });
};

export const computeFilteredBalance = (
    statement: ProviderPatientStatement | undefined,
    filteredStatementData: IStatementData[],
    dosFilterFrom: Date | null,
    dosFilterTo: Date | null,
): string => {
    if (!dosFilterFrom && !dosFilterTo) {
        return statement?.balanceDue?.amount || '0.00';
    }
    const total = filteredStatementData.reduce((sum, r) => sum + parseFloat(r.balance?.amount || '0'), 0);
    return total.toFixed(2);
};

export const buildServicesRows = (args: {
    statement: ProviderPatientStatement | undefined;
    professionals: ProviderProfessionalClaim[];
    dosFilterFrom: Date | null;
    dosFilterTo: Date | null;
}): ServicesTableRow[] => {
    const { statement, professionals, dosFilterFrom, dosFilterTo } = args;
    const rows: ServicesTableRow[] = [];

    for (const claim of statement?.claims || []) {
        if (!isInRange(claim.fromDos, dosFilterFrom, dosFilterTo)) continue;
        for (const line of claim.serviceLines || []) {
            const currentProfessional = _.find(professionals, { claimId: claim.id });
            let diagnosisCodes: string[] = [];
            if (currentProfessional?.professionalTemplate) {
                const serviceLineDetails = _.find(currentProfessional.professionalTemplate.f24ServiceLine, {
                    id: line.id,
                });
                if (serviceLineDetails) {
                    const serviceLinePointers = serviceLineDetails.f24eDiagnosisPointers || [];
                    const diagnosisCodeMap = extractDiagnosisCodes(currentProfessional.professionalTemplate);
                    diagnosisCodes = serviceLinePointers
                        .map((letter: string) => diagnosisCodeMap[letter.toLowerCase()])
                        .filter(Boolean);
                }
            }

            rows.push({
                id: `${claim.id || claim.externalId || 'claim'}-${line.id || line.procedure || rows.length}`,
                procedure: line.procedure,
                description: line.description,
                claimId: claim.externalId || claim.id,
                date: claim.fromDos,
                charge: line.chargeAmount?.amount,
                diagnosisCodes,
            });
        }
    }

    return rows.sort((a, b) => {
        const aTime = a.date ? new Date(a.date).getTime() : 0;
        const bTime = b.date ? new Date(b.date).getTime() : 0;
        return bTime - aTime;
    });
};

/** Allocate row for an unapplied refund: allocating it posts a NEGATIVE patient payment to the claim. */
export const isRefundAllocation = (row?: IStatementData): boolean =>
    row?.type === 'unallocated' && parseMoney(row.credit?.amount) < 0;

export const buildPaymentsTableRows = (
    filteredPaymentsData: IPaymentData[],
    statement: ProviderPatientStatement | undefined,
): PaymentsTableRow[] => {
    const statementClaims = statement?.claims || [];
    return filteredPaymentsData.map(payment => {
        const associatedClaims = statementClaims.filter(claim =>
            (claim.patientPayments || []).some(pp => pp.collectionPaymentSourceId === payment.id),
        );
        const linkedPayments = statementClaims.flatMap(claim =>
            (claim.patientPayments || [])
                .filter(pp => pp.collectionPaymentSourceId === payment.id)
                .map(pp => ({
                    payment: pp,
                    claimId: claim.id,
                    externalId: claim.externalId,
                    pending: !!claim.pendingAdjudication,
                })),
        );
        const total = parseMoney(payment.totalAmount?.amount);
        const refund = parseMoney(payment.refundAmount?.amount);
        const unallocated = parseMoney(payment.unallocatedAmount?.amount);
        const allocated = parseMoney(payment.allocatedAmount?.amount);
        const isRefund = refund > 0 && total <= 0;
        const typeLabel = isRefund ? 'Refund' : 'Patient payment';
        // A negative source with a negative remainder is a refund not yet applied to a claim.
        // A positive source with a negative remainder is over-allocated — not something to allocate further.
        const canAllocate = unallocated > 0 || (unallocated < 0 && total < 0);
        const applied =
            associatedClaims.length === 0
                ? canAllocate
                    ? 'Unallocated'
                    : '—'
                : associatedClaims
                      .map(claim => `#${claim.externalId || claim.id}`)
                      .filter(Boolean)
                      .join(', ') || '—';

        // A remainder must not hide Edit: the posted amount can still be moved or reduced.
        const allocateRow: IStatementData | undefined =
            canAllocate && payment.id
                ? {
                      id: `${payment.id}-unallocated`,
                      claimId: payment.id,
                      type: 'unallocated',
                      date: payment.paymentDate,
                      description: 'Patient Payment (Unallocated)',
                      credit: payment.unallocatedAmount,
                  }
                : undefined;

        const editRows: IStatementData[] = linkedPayments.flatMap(item => {
            if (!item.claimId || !item.payment.id) {
                return [];
            }
            const claimNumber = item.externalId || item.claimId;
            return [
                {
                    id: `${item.claimId}-allocated-${item.payment.id}`,
                    claimId: item.claimId,
                    type: 'allocated' as const,
                    date: item.payment.paymentDate,
                    description: `#${claimNumber}`,
                    credit: item.payment.amount,
                    metadata: {
                        pending: item.pending,
                        id: item.payment.id,
                    },
                },
            ];
        });

        return {
            id: payment.id || `${payment.paymentDate}-${payment.externalTransactionId || ''}`,
            date: payment.paymentDate,
            type: typeLabel,
            ref: payment.externalTransactionId || (payment.id ? `ID ${payment.id}` : undefined),
            applied,
            amount: isRefund ? refund : total,
            allocatedAmount: allocated,
            unallocatedAmount: unallocated,
            tone: (isRefund ? 'warning' : 'credit') as 'warning' | 'credit',
            allocateRow,
            editRows,
        };
    });
};

export const formatStatementAddress = (addresses?: Array<{
    lines?: string[];
    city?: string;
    state?: string;
    postal?: string;
}>): string => {
    if (!addresses || addresses.length === 0) return '';
    const address = addresses[0];
    const lines = address.lines?.join(', ') || '';
    const cityStateZip = `${address.city || ''}, ${address.state || ''} ${address.postal || ''}`.trim();
    return `${lines}${lines && cityStateZip ? ', ' : ''}${cityStateZip}`;
};
